import { createExponentialBackoff } from './chunk-WDGAR7LI.mjs';
import { useTracker } from './chunk-TTQ65QRH.mjs';
import { createConnectionError } from './chunk-JWOI4Q4B.mjs';
import { useAuthContext } from './chunk-HVKTN277.mjs';
import { __async, __spreadValues } from './chunk-YUBKM3FG.mjs';
import { useState, useMemo, useCallback, useEffect } from 'react';

function useSubscription(params) {
  const { hasuraUrl, query, variables, enabled } = params;
  const [state, setState] = useState({
    data: null,
    isLoading: true,
    error: null
  });
  const { isSignedIn, getAccessToken } = useAuthContext();
  const backoff = useMemo(() => createExponentialBackoff(), []);
  const { captureError } = useTracker();
  const memoizedVariables = useMemo(
    () => variables,
    // Only recreate when the stringified version changes
    [JSON.stringify(variables)]
  );
  const errorContext = useMemo(
    () => ({
      query,
      variables: memoizedVariables
    }),
    [query, memoizedVariables]
  );
  const fingerprint = ["{{ default }}", "useSubscription"];
  const initializeConnection = useCallback(
    (ws) => __async(this, null, function* () {
      try {
        const token = isSignedIn ? yield getAccessToken() : void 0;
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        ws.send(
          JSON.stringify({
            type: "connection_init",
            payload: {
              headers
            }
          })
        );
        backoff.reset();
      } catch (err) {
        const error = createConnectionError(err instanceof Error ? err : new Error("Failed to init connection"));
        captureError(error, {
          tags: [
            { key: "category", value: "websocket" },
            { key: "error_type", value: "CONNECTION_INIT_FAILED" /* CONNECTION_INIT_FAILED */ }
          ],
          extras: __spreadValues({}, errorContext),
          fingerprint
        });
        handleConnectionError(createWebSocketConnection);
      }
    }),
    [query, memoizedVariables, backoff, isSignedIn, getAccessToken]
  );
  const startSubscription = useCallback(
    (connection) => {
      const { ws, subscriptionId } = connection;
      const startMessage = {
        id: subscriptionId,
        type: "start",
        payload: {
          query,
          variables: memoizedVariables
        }
      };
      ws.send(JSON.stringify(startMessage));
    },
    [query, memoizedVariables]
  );
  const handleConnectionError = useCallback(
    (createWebSocketConnection2) => {
      if (backoff.shouldRetry()) {
        const delay = backoff.getNextDelay();
        setTimeout(createWebSocketConnection2, delay);
      } else {
        setState({
          data: null,
          isLoading: false,
          error: createConnectionError(new Error("Max reconnection attempts exceeded"))
        });
      }
    },
    [backoff]
  );
  const handleSubscriptionMessage = useCallback(
    (connection) => (event) => {
      var _a;
      try {
        const { ws } = connection;
        const message = JSON.parse(event.data);
        switch (message.type) {
          case "connection_ack": {
            startSubscription(connection);
            break;
          }
          case "data": {
            setState({
              data: message.payload.data,
              isLoading: false,
              error: null
            });
            break;
          }
          case "error": {
            const errorMessage = ((_a = message.payload) == null ? void 0 : _a.message) || "Subscription error";
            const error = createConnectionError(new Error(errorMessage));
            captureError(error, {
              tags: [
                { key: "category", value: "websocket" },
                { key: "error_type", value: "DATA_PARSING_ERROR" /* DATA_PARSING_ERROR */ }
              ],
              extras: __spreadValues({}, errorContext),
              fingerprint
            });
            setState({
              data: null,
              isLoading: false,
              error
            });
            break;
          }
          case "complete": {
            try {
              ws.close();
            } catch (err) {
              const error = createConnectionError(err instanceof Error ? err : new Error("Failed to close connection"));
              captureError(error, {
                tags: [
                  { key: "category", value: "websocket" },
                  { key: "error_type", value: "CONNECTION_CLOSED" /* CONNECTION_CLOSED */ }
                ],
                extras: __spreadValues({}, errorContext),
                fingerprint
              });
            }
            break;
          }
          default: {
            break;
          }
        }
      } catch (err) {
        const error = createConnectionError(err instanceof Error ? err : new Error("Parse websocket message error"));
        captureError(error, {
          tags: [
            { key: "category", value: "websocket" },
            { key: "error_type", value: "DATA_PARSING_ERROR" /* DATA_PARSING_ERROR */ }
          ],
          extras: __spreadValues({}, errorContext),
          fingerprint
        });
        setState({
          data: null,
          isLoading: false,
          error
        });
      }
    },
    [startSubscription]
  );
  const createWebSocketConnection = useCallback(() => {
    const wsUrl = hasuraUrl.replace("https://", "wss://");
    const ws = new WebSocket(wsUrl, "graphql-ws");
    const subscriptionId = Math.random().toString(36).substring(2, 11);
    const connection = { ws, subscriptionId };
    ws.onopen = () => __async(this, null, function* () {
      return yield initializeConnection(ws);
    });
    ws.onerror = () => handleConnectionError(createWebSocketConnection);
    ws.onmessage = handleSubscriptionMessage(connection);
    return connection;
  }, [hasuraUrl, initializeConnection, handleConnectionError, handleSubscriptionMessage]);
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const { ws, subscriptionId } = createWebSocketConnection();
    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        try {
          const stopMessage = {
            id: subscriptionId,
            type: "stop"
          };
          ws.send(JSON.stringify(stopMessage));
          ws.close();
        } catch (err) {
          const error = createConnectionError(err instanceof Error ? err : new Error("Cleanup failed"));
          captureError(error, {
            tags: [
              { key: "category", value: "websocket" },
              { key: "error_type", value: "CLEANUP_ERROR" /* CLEANUP_ERROR */ }
            ],
            extras: __spreadValues({}, errorContext),
            fingerprint
          });
        }
      }
    };
  }, [createWebSocketConnection, enabled]);
  if (!enabled) {
    return {
      data: null,
      isLoading: true,
      error: null
    };
  }
  return state;
}

export { useSubscription };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=chunk-CI2YV475.mjs.map